/**
 * CADASTRO RÁPIDO — criar o que falta sem sair de onde se está.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * O PROBLEMA QUE ISTO RESOLVE
 * ─────────────────────────────────────────────────────────────────────────────
 * Abrir uma não-conformidade exige escolher departamento e responsável. Se o
 * departamento não existe, o caminho era: abandonar o formulário, ir a
 * Usuários › Departamentos, criar, voltar, e redigitar tudo o que já tinha sido
 * escrito. Na prática, quem registra desvio no chão de fábrica escolhia o
 * departamento errado — porque redigitar é pior do que errar.
 *
 * Cada diálogo aqui cria UMA coisa, com o mínimo de campos, e devolve o
 * identificador para o combobox selecionar. O que estava digitado atrás
 * continua lá: o diálogo não desmonta o formulário de trás.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * O MÍNIMO É DELIBERADO
 * ─────────────────────────────────────────────────────────────────────────────
 * Nenhum destes diálogos reproduz a tela de administração inteira. O
 * departamento nasce só com nome; a hierarquia, os gerentes e a descrição ficam
 * para a tela própria. Um formulário de dez campos no meio de outro formulário
 * é troca de contexto disfarçada de atalho — e a pessoa que só queria continuar
 * o que estava fazendo passaria a decidir coisas que não vieram ao caso.
 *
 * O que NÃO tem cadastro rápido, e por quê:
 *   · NÃO-CONFORMIDADE — tem matriz GUT, evidência e data do fato. Não cabe
 *     num diálogo, e abrir uma NC "de passagem" enquanto se faz outra coisa é
 *     como nasce registro pela metade num sistema regulado.
 * COLABORADOR tem cadastro rápido, com uma ressalva: o PAPEL é fixo em MEMBER e
 * o departamento fica vazio. Papel é autoridade (§11.10(g)) e escolher
 * autoridade de passagem é como alguém vira aprovador por engano. Promover e
 * lotar são atos próprios, na tela de Usuários.
 */

import {
  createMeetingTypeSchema,
  createRequestTypeSchema,
  departmentSchema,
  documentTypeSchema,
  SystemRole,
  type DepartmentInput,
} from '@qualicloud/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  Button,
  Dialog,
  Field,
  FormError,
  Input,
  useToast,
} from '@/design-system';
import {
  departmentsApi,
  documentTypesApi,
  meetingTypesApi,
  queryKeys,
  requestTypesApi,
  toUserMessage,
  usersApi,
} from '@/lib/api';

interface PropsDeCadastro {
  /** O que a pessoa digitou no combobox antes de descobrir que não existia. */
  readonly termo: string;
  readonly aoConcluir: (value: string) => void;
  readonly aoFechar: () => void;
}

/** Casca comum: título, erro, rodapé e o Enter que salva. */
function DialogoDeCadastro({
  titulo,
  descricao,
  erro,
  salvando,
  podeSalvar,
  aoSalvar,
  aoFechar,
  children,
}: {
  readonly titulo: string;
  readonly descricao: string;
  readonly erro: string | null;
  readonly salvando: boolean;
  readonly podeSalvar: boolean;
  readonly aoSalvar: () => void;
  readonly aoFechar: () => void;
  readonly children: React.ReactNode;
}) {
  return (
    <Dialog
      open
      onOpenChange={(aberto) => {
        if (!aberto) aoFechar();
      }}
      title={titulo}
      description={descricao}
      footer={
        <>
          <Button variant="secondary" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button onClick={aoSalvar} disabled={salvando || !podeSalvar}>
            {salvando ? 'Criando…' : 'Criar'}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(evento) => {
          /*
           * Enter salva AQUI, e só aqui.
           *
           * `preventDefault` sozinho não bastava, e o comentário anterior
           * descrevia uma proteção que não existia: este diálogo é portalizado
           * para o `document.body`, mas o evento `submit` do React propaga pela
           * árvore de FIBRAS, não pela do DOM — e o diálogo é renderizado dentro
           * do `<form>` externo (`select.tsx`, `combobox.tsx`). Sem
           * `stopPropagation`, o mesmo Enter chegava ao `onSubmit` de fora:
           * criava a solicitação 5W2H com o tipo antigo, abria a
           * não-conformidade ou marcava a reunião — registro regulado nascendo
           * sem ninguém ter pedido, com este diálogo ainda na tela.
           */
          evento.preventDefault();
          evento.stopPropagation();
          if (podeSalvar && !salvando) aoSalvar();
        }}
      >
        {erro ? <FormError>{erro}</FormError> : null}
        {children}
        <button type="submit" className="qc-sr-only" aria-hidden tabIndex={-1} />
      </form>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Departamento
// ─────────────────────────────────────────────────────────────────────────────

export function CadastroRapidoDeDepartamento({ termo, aoConcluir, aoFechar }: PropsDeCadastro) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [nome, setNome] = useState(termo);
  const [erro, setErro] = useState<string | null>(null);

  const criar = useMutation({
    mutationFn: () => {
      const entrada: DepartmentInput = departmentSchema.parse({ name: nome.trim() });
      return departmentsApi.create(entrada);
    },
    onError: (error) => setErro(toUserMessage(error)),
    onSuccess: async (criado) => {
      /*
       * O DIRETÓRIO também é invalidado, e não só a árvore de departamentos:
       * é ele que alimenta os seletores em seis telas do SGQ, com cache de 5
       * minutos. Sem isto o departamento recém-criado não apareceria na lista
       * de onde ele foi criado.
       */
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.departments.all() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.directory.all() }),
      ]);
      toast({ tone: 'success', title: 'Departamento criado', description: criado.name });
      aoConcluir(criado.id);
    },
  });

  return (
    <DialogoDeCadastro
      titulo="Novo departamento"
      descricao="Só o nome. Hierarquia, gerentes e membros ficam para a tela de Departamentos."
      erro={erro}
      salvando={criar.isPending}
      podeSalvar={nome.trim().length > 0}
      aoSalvar={() => criar.mutate()}
      aoFechar={aoFechar}
    >
      <Field label="Nome" required>
        <Input autoFocus value={nome} onChange={(e) => setNome(e.target.value)} />
      </Field>
    </DialogoDeCadastro>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Colaborador
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Deriva usuário e senha inicial do que foi digitado.
 *
 * O nome de usuário é ÚNICO e PERMANENTE (§11.300(a)) — não pode ser alterado
 * depois, nem reciclado. Deixar quem está no meio de uma NC inventá-lo é pedir
 * "jsilva2" e uma correção impossível; derivá-lo de nome e sobrenome dá um
 * padrão previsível para a organização inteira.
 */
function usuarioSugerido(nome: string, sobrenome: string): string {
  const limpar = (t: string) =>
    t
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  return [limpar(nome), limpar(sobrenome)].filter(Boolean).join('.');
}

/**
 * Senha inicial GERADA, mostrada uma vez.
 *
 * `crypto.getRandomValues`, e nunca `Math.random()`: é a credencial de acesso
 * de uma pessoa a um sistema que assina registro com valor legal. O titular é
 * obrigado a trocá-la no primeiro acesso.
 */
function senhaInicial(): string {
  const ALFABETO = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*?';
  const bytes = new Uint32Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => ALFABETO[b % ALFABETO.length]).join('');
}

/**
 * CADASTRO RÁPIDO DE COLABORADOR — quatro campos, e o resto tem padrão.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * O QUE NÃO SE ESCOLHE AQUI, E POR QUÊ
 * ─────────────────────────────────────────────────────────────────────────────
 * O PAPEL é fixo em MEMBER. Papel é autoridade — quem aprova documento, quem
 * encerra NC —, e §11.10(g) pede que o sistema garanta que só quem tem
 * autoridade execute o ato. Escolher autoridade de passagem, no meio de outro
 * formulário, é como alguém vira aprovador por engano. Promover é ato próprio,
 * na tela de Usuários, com justificativa obrigatória.
 *
 * O DEPARTAMENTO também não: quem está criando a pessoa aqui está escolhendo
 * responsável de UMA NC, e a lotação dela é outra decisão.
 *
 * O CARGO é pedido porque ele é CONGELADO em cada assinatura eletrônica que a
 * pessoa emitir (§11.50(a)). Criar alguém com cargo vazio produziria assinatura
 * sem o cargo do signatário — que é dado obrigatório do manifesto.
 */
export function CadastroRapidoDeColaborador({ termo, aoConcluir, aoFechar }: PropsDeCadastro) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // O termo digitado costuma ser o nome completo: "Ana Paula Souza".
  const partes = termo.trim().split(/\s+/).filter(Boolean);
  const [nome, setNome] = useState(partes[0] ?? '');
  const [sobrenome, setSobrenome] = useState(partes.slice(1).join(' '));
  const [email, setEmail] = useState('');
  const [cargo, setCargo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [criado, setCriado] = useState<{ id: string; username: string; senha: string } | null>(null);

  const usuario = usuarioSugerido(nome, sobrenome);

  const criar = useMutation({
    mutationFn: () => {
      const senha = senhaInicial();
      return usersApi
        .create({
          username: usuario,
          email: email.trim(),
          firstName: nome.trim(),
          lastName: sobrenome.trim(),
          jobTitle: cargo.trim(),
          role: SystemRole.MEMBER,
          departmentIds: [],
          active: true,
          initialPassword: senha,
        })
        .then((r) => ({ ...r, senha }));
    },
    onError: (error) => setErro(toUserMessage(error)),
    onSuccess: async (resultado) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.users.all() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.directory.all() }),
      ]);
      toast({ tone: 'success', title: 'Colaborador cadastrado' });
      setCriado({ id: resultado.id, username: resultado.username, senha: resultado.senha });
    },
  });

  /*
   * A senha aparece UMA vez, numa segunda etapa. Fechar sem passar por aqui
   * perderia a única cópia — o que fica no banco é o hash Argon2id.
   */
  if (criado) {
    return (
      <Dialog
        open
        onOpenChange={() => aoConcluir(criado.id)}
        title="Colaborador cadastrado"
        description="Anote a senha inicial: ela não pode ser recuperada depois."
        /*
         * NÃO se fecha por Esc, clique fora ou X — só pelo "Continuar".
         *
         * Antes os três caminhos caíam em `aoConcluir`, e um Esc reflexo
         * descartava a senha. Ela não existe em nenhum outro lugar: o banco
         * guarda o hash Argon2id, e não há tela de redefinir senha nem de
         * "esqueci minha senha" na aplicação — `resetPassword` está declarada
         * em `admin.api.ts` e não tem um único chamador. Perder esta tela
         * deixava a conta recém-criada inacessível, em definitivo.
         */
        dismissible={false}
        footer={<Button onClick={() => aoConcluir(criado.id)}>Continuar</Button>}
      >
        <dl className="flex flex-col gap-3">
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-content-subtle">
              Usuário
            </dt>
            <dd className="font-mono text-base text-content">{criado.username}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-content-subtle">
              Senha inicial
            </dt>
            <dd className="rounded-md border border-border-control bg-surface-sunken px-3 py-2 font-mono text-base tracking-wide text-content">
              {criado.senha}
            </dd>
          </div>
        </dl>
        <p className="mt-3 text-sm text-content-muted">
          A conta entra com o papel Colaborador e sem departamento. Para promover ou lotar, use a
          tela de Usuários.
        </p>
      </Dialog>
    );
  }

  return (
    <DialogoDeCadastro
      titulo="Novo colaborador"
      descricao="A conta nasce com o papel Colaborador e senha temporária. Papel e departamento se definem em Usuários."
      erro={erro}
      salvando={criar.isPending}
      podeSalvar={
        nome.trim().length > 0 &&
        sobrenome.trim().length > 0 &&
        email.trim().length > 0 &&
        cargo.trim().length > 0
      }
      aoSalvar={() => criar.mutate()}
      aoFechar={aoFechar}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Nome" required>
          <Input autoFocus value={nome} onChange={(e) => setNome(e.target.value)} />
        </Field>
        <Field label="Sobrenome" required>
          <Input value={sobrenome} onChange={(e) => setSobrenome(e.target.value)} />
        </Field>
      </div>
      <Field label="E-mail" required>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </Field>
      <Field
        label="Cargo"
        required
        description="Congelado em cada assinatura eletrônica que a pessoa emitir — §11.50(a)."
      >
        <Input value={cargo} onChange={(e) => setCargo(e.target.value)} />
      </Field>
      {usuario ? (
        <p className="text-sm text-content-muted">
          Nome de usuário: <span className="font-mono text-content">{usuario}</span> — único e
          permanente, não pode ser alterado depois.
        </p>
      ) : null}
    </DialogoDeCadastro>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Catálogos de tipo — reunião, solicitação, documento
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Os três catálogos têm a mesma forma — código e nome — e flags que mudam o
 * comportamento do SGQ (exigir NC vinculada, exigir causa-raiz, exigir plano
 * completo). As FLAGS ficam de fora do cadastro rápido de propósito: elas
 * decidem o que o sistema vai cobrar de todo registro daquele tipo, e escolher
 * isso de passagem é como um tipo nasce exigindo o que ninguém queria.
 *
 * O tipo nasce com os padrões e a tela do catálogo os ajusta.
 */
function useCadastroDeTipo(
  criarNoServidor: (entrada: { code: string; name: string }) => Promise<{ id: string; name: string }>,
  chaveDeCache: readonly unknown[],
  rotulo: string,
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [erro, setErro] = useState<string | null>(null);

  const mutacao = useMutation({
    mutationFn: criarNoServidor,
    onError: (error) => setErro(toUserMessage(error)),
    onSuccess: async (criado) => {
      await queryClient.invalidateQueries({ queryKey: chaveDeCache });
      toast({ tone: 'success', title: `${rotulo} criado`, description: criado.name });
    },
  });

  return { erro, setErro, mutacao };
}

/**
 * O código sai do nome, e é editável.
 *
 * Ele identifica o tipo em relatório e em integração, então não pode ter acento
 * nem espaço. Deixar a pessoa inventá-lo do zero é pedir um campo que ela não
 * sabe preencher; derivá-lo sem deixar corrigir é decidir por ela.
 */
function codigoSugerido(nome: string): string {
  return nome
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 32);
}

function CadastroRapidoDeTipo({
  termo,
  aoConcluir,
  aoFechar,
  titulo,
  descricao,
  criarNoServidor,
  chaveDeCache,
  rotulo,
}: PropsDeCadastro & {
  readonly titulo: string;
  readonly descricao: string;
  readonly criarNoServidor: (entrada: {
    code: string;
    name: string;
  }) => Promise<{ id: string; name: string }>;
  readonly chaveDeCache: readonly unknown[];
  readonly rotulo: string;
}) {
  const [nome, setNome] = useState(termo);
  const [codigo, setCodigo] = useState(codigoSugerido(termo));
  const [codigoTocado, setCodigoTocado] = useState(false);
  const { erro, mutacao } = useCadastroDeTipo(criarNoServidor, chaveDeCache, rotulo);

  return (
    <DialogoDeCadastro
      titulo={titulo}
      descricao={descricao}
      erro={erro}
      salvando={mutacao.isPending}
      podeSalvar={nome.trim().length > 0 && codigo.trim().length > 0}
      aoSalvar={() =>
        mutacao.mutate(
          { code: codigo.trim(), name: nome.trim() },
          { onSuccess: (criado) => aoConcluir(criado.id) },
        )
      }
      aoFechar={aoFechar}
    >
      <Field label="Nome" required>
        <Input
          autoFocus
          value={nome}
          onChange={(e) => {
            setNome(e.target.value);
            // Para de sugerir assim que a pessoa edita o código à mão — senão o
            // sistema sobrescreveria, a cada tecla do nome, o que ela acabou de
            // corrigir.
            if (!codigoTocado) setCodigo(codigoSugerido(e.target.value));
          }}
        />
      </Field>
      <Field
        label="Código"
        required
        description="Identifica o tipo em relatórios e integrações. Letras maiúsculas, números e sublinhado."
      >
        <Input
          value={codigo}
          onChange={(e) => {
            setCodigoTocado(true);
            setCodigo(e.target.value.toUpperCase());
          }}
        />
      </Field>
    </DialogoDeCadastro>
  );
}

export function CadastroRapidoDeTipoDeReuniao(props: PropsDeCadastro) {
  return (
    <CadastroRapidoDeTipo
      {...props}
      titulo="Novo tipo de reunião"
      descricao="As regras do tipo — exigir NC vinculada, exigir verificação de eficácia — ficam para a tela de Tipos de reunião."
      /*
       * O schema PREENCHE os padrões das flags. Montá-los à mão aqui criaria um
       * segundo lugar onde o padrão de um tipo é decidido — e os dois
       * divergiriam na primeira mudança.
       */
      criarNoServidor={(entrada) => meetingTypesApi.create(createMeetingTypeSchema.parse(entrada))}
      chaveDeCache={queryKeys.meetingTypes.all()}
      rotulo="Tipo de reunião"
    />
  );
}

export function CadastroRapidoDeTipoDeSolicitacao(props: PropsDeCadastro) {
  return (
    <CadastroRapidoDeTipo
      {...props}
      titulo="Novo tipo de solicitação"
      descricao="As regras do tipo — exigir causa-raiz, exigir plano 5W2H completo — ficam para a tela de Tipos de solicitação."
      criarNoServidor={(entrada) => requestTypesApi.create(createRequestTypeSchema.parse(entrada))}
      chaveDeCache={queryKeys.requestTypes.all()}
      rotulo="Tipo de solicitação"
    />
  );
}

/**
 * Tipo de documento NÃO tem código — ele se identifica pelo nome e pela posição
 * na árvore. Por isso não reusa `CadastroRapidoDeTipo`: forçar um campo de
 * código aqui seria inventar um dado que o resto do sistema não conhece.
 */
export function CadastroRapidoDeTipoDeDocumento({ termo, aoConcluir, aoFechar }: PropsDeCadastro) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [nome, setNome] = useState(termo);
  const [erro, setErro] = useState<string | null>(null);

  const criar = useMutation({
    mutationFn: () => documentTypesApi.create(documentTypeSchema.parse({ name: nome.trim() })),
    onError: (error) => setErro(toUserMessage(error)),
    onSuccess: async (criado) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.documentTypes.all() });
      toast({ tone: 'success', title: 'Tipo de documento criado', description: nome.trim() });
      aoConcluir(criado.id);
    },
  });

  return (
    <DialogoDeCadastro
      titulo="Novo tipo de documento"
      descricao="Numeração, validade padrão e política de aprovação ficam para a tela de Tipos de documento."
      erro={erro}
      salvando={criar.isPending}
      podeSalvar={nome.trim().length > 0}
      aoSalvar={() => criar.mutate()}
      aoFechar={aoFechar}
    >
      <Field label="Nome" required>
        <Input autoFocus value={nome} onChange={(e) => setNome(e.target.value)} />
      </Field>
    </DialogoDeCadastro>
  );
}
